import {
  types,
  getParent,
  ISimpleType,
  Instance,
  IModelType,
  SnapshotIn
} from 'mobx-state-tree'
import * as THREE from 'three'
import { generateNodeType } from 'src/api/models/utils/generateNodeType'
import {
  editor,
  printedcircuitboard,
  cover,
  elementEdge,
  project,
  contentEdge,
  coverEdge,
  textEngravingElement
} from 'src/constants/typenames'
import { default as sides, Side, orbit } from 'src/constants/sides'
import { ElementEdge } from 'src/api/models/Elements/ElementEdge'
import { Color } from 'src/api/misc/Color'
import { Printedcircuitboard } from 'src/api/models/Content/Printedcircuitboard'
import { Cover, CoverEdge } from 'src/api/models/Enclosure'
import { Project } from 'src/api/models/Project'
import { Total } from 'src/api/stores/Total'
// import { Camera } from 'src/api/models/Camera'
import * as matrices from 'src/constants/matrices'
import {
  CurrentView,
  EditorOrbitview,
  EditorSideview
} from 'src/api/stores/Editor/EditorView'
import { fromGlobalId } from 'src/api/utils/globalId'
import { ContentEdge } from 'src/api/models/Content/ContentEdge'
import { generateDrafter } from 'src/api/misc/Drafter'
import { Padding } from 'src/api/misc/View/Padding'
import { Draft } from 'src/api/misc/View'
import { ISelectedItem, IEditor, IEditorModel } from './types'
import { IControlHandlerKey } from 'src/api/misc/View/types'
import { IElementEdge } from 'src/api/models/Elements/ElementEdge/types'
import { IColor } from 'src/api/misc/Color/types'
import { views } from 'src/constants/views'

type SideviewIdentifier = Unpacked<typeof views>

type SelectedItem = Instance<
  | ReturnType<typeof ElementEdge | typeof Printedcircuitboard | typeof Project>
  | typeof Cover
>

type ProjectInstance = Instance<ReturnType<typeof Project>>
type ElementEdgeInstance = Instance<ReturnType<typeof ElementEdge>>
type ElementsGetter = { [key in Side]: ElementEdgeInstance[] }

const Editor = (): IEditor => {
  // @ts-ignore
  const EditorModel: IEditorModel = generateNodeType(editor)
    .props({
      draft: Draft,
      sideviews: types.optional(
        types.map(types.union(EditorSideview, EditorOrbitview)),
        views.reduce((p, side) => ({ ...p, [side]: { side } }), {})
      ),
      currentView: types.optional(CurrentView, orbit),
      padding: types.optional(Padding, {
        top: 0,
        right: 320,
        bottom: 0,
        left: 0
      }),
      total: types.optional(types.late(Total) as any, {})
    })
    .views((self) => {
      return {
        get color() {
          const color =
            self.draft.enclosure.coverEdges[0].node.properties.color.value
          return color as Instance<IColor>
        },
        get elements(): ElementsGetter {
          return self.draft.enclosure.coverEdges[0].node.plates.edges.reduce(
            (p, edge: any) => {
              const side = edge.properties.side
              return {
                ...p,
                [side]: [...edge.node.elements.edges]
              }
            },
            {} as ElementsGetter
          )
        }
      }
    })
    .actions((self) => ({
      switchSide(side: SideviewIdentifier) {
        const sideInstance = self.sideviews.get(side)
        if (sideInstance !== undefined) {
          self.currentView.blur()
          self.currentView.disable()
          self.currentView = sideInstance as $LibBugAny
          self.currentView.enable()
          self.currentView.focus()
        }
      }
    }))
    .actions((self) => ({
      afterAttach() {
        if (self.currentView.selectedItem === null) {
          self.currentView.selectItem(self.draft)
        }
        self.currentView.enable()
        self.currentView.focus()
      },
      selectItem(item: ISelectedItem) {
        if (self.currentView.side === orbit) {
          if (item.__typename === elementEdge) {
            self.switchSide(item.side)
          }
        }
        self.currentView?.selectItem(item)
      },
      unselectItem() {
        self.currentView?.unselectItem()
      }
    }))
    .actions((self) => ({
      addElement(
        side: Side,
        input:
          | ElementEdgeInstance['node']['__typename']
          | SnapshotIn<IElementEdge>,
        switchSide?: boolean
      ) {
        const coverPlateEdge = self.draft.enclosure.coverEdges[0].node.plates.edges.find(
          (edge) => edge.properties.side === side
        )
        if (coverPlateEdge) {
          if (switchSide && side !== self.currentView.side)
            self.switchSide(side)
          const newElement = coverPlateEdge.node.elements?.addElement(
            input,
            true
          )
          self.selectItem(newElement)
        }
      },
      ...([
        'enable' as const,
        'disable' as const,
        'enableZoom' as const,
        'disableZoom' as const,
        'enablePan' as const,
        'disablePan' as const
      ].reduce(
        (p, c) => ({ ...p, [c]: () => self.currentView[c]?.() }),
        {}
      ) as {
        [key in IControlHandlerKey]: () => void
      })
    }))
    .views((self) => ({
      get price() {
        const price = 0.25
        const boundings = new THREE.Vector3()
        self.draft.boundings.getSize(boundings)
        const coverPrice = boundings
          .toArray()
          .reduce((p, n) => p + n * price, 0)
        const elementCount = Object.values(self.elements).reduce(
          (prev, list) => prev + list.length,
          0
        )
        // self.draft.enclosure.coverEdges.map(e => e.node.)
        return coverPrice + elementCount * (price * 4)
      }
    }))
  return generateDrafter(EditorModel)
}

export { Editor }

/**
 * Editor vs Setup
 *
 * Editor:
 *  - draft
 *  state:
 *  - selectedItem
 *  - currentSide
 *
 *
 *
 *
 * Setup:
 *  - draft
 *  state:
 * Dialog:
 *  - currentRoute
 *  - totals
 *
 *
 */
